import { useState } from 'react';
import {
  Form,
  InputNumber,
  Select,
  Input,
  Button,
  Card,
  Typography,
  Descriptions,
  Spin,
} from 'antd';
import { useCreateAdjustment, useStock } from '@hooks/useWarehouse';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import type { StockItem } from '@/types/warehouse';

const { TextArea } = Input;
const { Title } = Typography;

const REASON_OPTIONS = [
  { value: 'Інвентаризація', label: 'Інвентаризація' },
  { value: 'Пошкодження', label: 'Пошкодження' },
  { value: 'Помилка введення', label: 'Помилка введення' },
  { value: 'Інше', label: 'Інше' },
];

interface AdjustmentFormValues {
  batchId: string;
  newQuantity: number;
  reason: string;
  comment?: string;
}

export default function AdjustmentForm() {
  const [mdForm] = Form.useForm<AdjustmentFormValues>();
  const mdCreateAdjustment = useCreateAdjustment();
  const [mdSelectedBatch, setMdSelectedBatch] = useState<StockItem | null>(null);
  // Пошук партій на сервері (SKU, назва, номер партії, серійний): сторінка залишків — до 100 рядків
  const [mdBatchSearch, setMdBatchSearch] = useState('');
  const mdDebouncedBatchSearch = useDebouncedValue(mdBatchSearch.trim());
  const { data: mdStockData, isFetching: mdStockFetching } = useStock({
    search: mdDebouncedBatchSearch || undefined,
    page: 1,
    pageSize: 100,
  });

  // Обрана партія лишається в опціях, навіть якщо новий пошук її не повертає
  const mdBatchItems = mdSelectedBatch && !(mdStockData?.items ?? []).some((mdItem) => mdItem.batchId === mdSelectedBatch.batchId)
    ? [mdSelectedBatch, ...(mdStockData?.items ?? [])]
    : (mdStockData?.items ?? []);

  const mdBatchOptions = mdBatchItems.map((mdItem) => ({
    value: mdItem.batchId,
    label: `${mdItem.sku} — ${mdItem.batchNumber} (${mdItem.quantityOnHand} од.)`,
    item: mdItem,
  }));

  const handleBatchChange = (mdValue: string) => {
    const mdFound = mdBatchOptions.find((mdOpt) => mdOpt.value === mdValue);
    setMdSelectedBatch(mdFound?.item ?? null);
  };

  const handleSubmit = async (mdValues: AdjustmentFormValues) => {
    // CreateAdjustmentCommand має лише Reason (до 500 символів) — коментар дописуємо до причини
    const mdComment = mdValues.comment?.trim();
    await mdCreateAdjustment.mutateAsync({
      batchId: mdValues.batchId,
      newQuantity: mdValues.newQuantity,
      reason: mdComment ? `${mdValues.reason}: ${mdComment}` : mdValues.reason,
    });
    mdForm.resetFields();
    setMdSelectedBatch(null);
  };

  return (
    <Card style={{ maxWidth: 700 }}>
      <Title level={5} style={{ marginBottom: 24 }}>
        Коригування залишків
      </Title>

      <Form
        form={mdForm}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="Партія"
          name="batchId"
          rules={[{ required: true, message: 'Оберіть партію' }]}
        >
          <Select
            showSearch
            placeholder="Пошук партії..."
            options={mdBatchOptions}
            onChange={handleBatchChange}
            filterOption={false}
            onSearch={setMdBatchSearch}
            notFoundContent={mdStockFetching ? <Spin size="small" /> : 'Нічого не знайдено'}
          />
        </Form.Item>

        {mdSelectedBatch && (
          <Descriptions
            bordered
            size="small"
            column={2}
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Продукт">
              {mdSelectedBatch.productName}
            </Descriptions.Item>
            <Descriptions.Item label="SKU">{mdSelectedBatch.sku}</Descriptions.Item>
            <Descriptions.Item label="Партія">
              {mdSelectedBatch.batchNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Поточна кількість">
              <Typography.Text strong>{mdSelectedBatch.quantityOnHand}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        )}

        <Form.Item
          label="Нова кількість"
          name="newQuantity"
          rules={[{ required: true, message: 'Вкажіть нову кількість' }]}
        >
          <InputNumber
            min={0}
            placeholder="Нова кількість"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label="Причина"
          name="reason"
          rules={[{ required: true, message: 'Оберіть причину' }]}
        >
          <Select
            placeholder="Оберіть причину коригування"
            options={REASON_OPTIONS}
          />
        </Form.Item>

        <Form.Item label="Коментар" name="comment">
          <TextArea
            rows={3}
            maxLength={450}
            placeholder="Додатковий коментар (необов'язково)"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mdCreateAdjustment.isPending}
            size="large"
          >
            Зберегти коригування
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
